import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '../data-table/data-table.tsx';
import { ErrorBoundary } from '../ErrorBoundary.tsx';
import { CreateUserModal } from '../CreateUserModal.tsx';
import { DeleteUserDialog } from '../DeleteUserDialog.tsx';
import { GenerateTempPasswordModal } from '../GenerateTempPasswordModal.tsx';
import { PermanentDeleteUserDialog } from '../PermanentDeleteUserDialog.tsx';
import { ResendWelcomeEmailDialog } from '../ResendWelcomeEmailDialog.tsx';
import { ResetPasswordModal } from '../ResetPasswordModal.tsx';
import { SendTestEmailDialog } from '../SendTestEmailDialog.tsx';
import { useSendTestEmail } from '../../hooks/useSendTestEmail.ts';
import { UserEditDialog } from './dialogs/UserEditDialog.tsx';
import { buildColumns } from './columns.tsx';
import { UsersTableToolbar } from './UsersTableToolbar.tsx';
import { useUsersTable } from './useUsersTable.ts';
import type { UserRowActionKind } from './UserRowActions.tsx';
import type { UsersTableRow, UsersTableScope } from './types.ts';
import type { UserWithCustomers } from '../../hooks/useUsers.ts';

export interface UsersTableProps {
  /**
   * Which users the table shows, and — as a consequence — which controls it
   * renders. `{ mode: 'all' }` is User Management; `{ mode: 'customer' }` is a
   * customer detail page, where the Customer column and the customer filter are
   * redundant because the route already fixes the customer.
   */
  scope: UsersTableScope;
}

/**
 * The shared users table: one component, two mounts.
 *
 * It owns the whole surface — toolbar, columns, row actions and all seven
 * dialogs — so `/users` and a customer detail page cannot drift apart again.
 * The dialog mounts, their `ErrorBoundary` wrappers and the selected-user
 * plumbing below are `UsersPage`'s, moved here unchanged; everything that used
 * to be re-derived per page now comes from one row model (`derive.ts`), one
 * status module (`lib/user-status.ts`) and one permission matrix
 * (`UserRowActions.tsx`).
 *
 * The component renders **no container of its own** — no card, no heading. The
 * mounting page owns that: `UsersPage` puts it under its own heading, and the
 * customer detail page will put it inside the card its Users section already
 * has. What used to be `/users`'s full-page spinner and full-page error card
 * are in-place states here, so whatever the page wrapped around the table stays
 * on screen while the query is in flight or has failed.
 *
 * `scope` is allowed to change exactly five things, and no dialog behaviour is
 * among them: hide the Customer column, hide the customer filter, restrict the
 * rows via `belongsTo`, pass `lockedCustomerId` so the dialogs disable the
 * fields that must not be edited from a customer page, and pick the
 * empty-state sentence so the customer page keeps its own wording.
 */
export function UsersTable({ scope }: UsersTableProps) {
  const {
    scope: activeScope,
    rows,
    scopedCount,
    customerOptions,
    stateOptions,
    isLoading,
    error,
    table,
    filters,
    setFilters,
    clear,
    isDirty,
    can,
    writes,
  } = useUsersTable(scope);

  const [selectedUser, setSelectedUser] = useState<UserWithCustomers | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [sendTestEmailOpen, setSendTestEmailOpen] = useState(false);
  const { sendTestEmail, defaultRecipient } = useSendTestEmail();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [generateTempPasswordModalOpen, setGenerateTempPasswordModalOpen] = useState(false);
  const [resendWelcomeEmailDialogOpen, setResendWelcomeEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);

  /**
   * The `setSelectedUser(user)`-then-open-one-flag plumbing the menu items used
   * to do inline, in one place now that the menu is shared and gated.
   */
  const onAction = useCallback((kind: UserRowActionKind, row: UsersTableRow) => {
    setSelectedUser(row.user);
    switch (kind) {
      case 'edit':
        return setEditModalOpen(true);
      case 'resendWelcomeEmail':
        return setResendWelcomeEmailDialogOpen(true);
      case 'resetPassword':
        return setResetPasswordModalOpen(true);
      case 'generateTempPassword':
        return setGenerateTempPasswordModalOpen(true);
      case 'disable':
        return setDeleteDialogOpen(true);
      case 'permanentDelete':
        return setPermanentDeleteDialogOpen(true);
    }
  }, []);

  const canSendTestEmail = can.create && activeScope.mode === 'all';

  const columns = useMemo(
    () => buildColumns(activeScope, { can, onAction }),
    [activeScope, can, onAction],
  );

  const lockedCustomerId = activeScope.mode === 'customer' ? activeScope.customerId : undefined;

  // "Nothing here yet" and "your filters excluded everything" are different
  // answers, and the shell renders whichever applies in the table body so the
  // headers and pagination stay in place either way.
  const tableEmptyMessage = scopedCount === 0
    ? (activeScope.mode === 'customer'
      ? 'No users are associated with this customer.'
      : 'No users found.')
    : 'No results.';

  return (
    <>
      <div className='space-y-4'>
        {error
          ? (
            <p className='text-destructive text-sm py-4 text-center'>
              Failed to load users: {error}
            </p>
          )
          : (
            <>
              <UsersTableToolbar
                scope={activeScope}
                filters={filters}
                setFilters={setFilters}
                clear={clear}
                isDirty={isDirty}
                customerOptions={customerOptions}
                stateOptions={stateOptions}
                can={can}
                onCreateUser={() => setCreateModalOpen(true)}
                // Test emails aren't customer-specific, so only the all-users page offers them
                onSendTestEmail={canSendTestEmail ? () => setSendTestEmailOpen(true) : undefined}
                disabled={isLoading}
              />
              {isLoading
                ? (
                  <p className='text-sm text-muted-foreground py-4 text-center'>
                    Loading users...
                  </p>
                )
                : (
                  // Nine columns; keep the overflow local to this table so no
                  // other consumer of the shared shell changes layout.
                  <div className='overflow-x-auto'>
                    <DataTable
                      columns={columns}
                      data={rows}
                      sorting={table.sorting}
                      onSortingChange={table.onSortingChange}
                      pagination={table.pagination}
                      onPaginationChange={table.onPaginationChange}
                      emptyMessage={tableEmptyMessage}
                    />
                  </div>
                )}
            </>
          )}
      </div>

      {/* Modals */}
      <ErrorBoundary>
        <CreateUserModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onCreateUser={writes.createUserWithoutInvalidation}
          lockedCustomerId={lockedCustomerId}
        />
      </ErrorBoundary>
      {canSendTestEmail && (
        <ErrorBoundary>
          <SendTestEmailDialog
            open={sendTestEmailOpen}
            onOpenChange={setSendTestEmailOpen}
            onSendTestEmail={sendTestEmail}
            defaultRecipient={defaultRecipient}
          />
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        <UserEditDialog
          open={editModalOpen}
          user={selectedUser}
          onOpenChange={setEditModalOpen}
          onUpdateUser={writes.updateUserWithoutInvalidation}
          lockedCustomerId={lockedCustomerId}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <ResetPasswordModal
          open={resetPasswordModalOpen}
          user={selectedUser}
          onOpenChange={setResetPasswordModalOpen}
          onResetPassword={writes.resetPassword}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <GenerateTempPasswordModal
          open={generateTempPasswordModalOpen}
          user={selectedUser}
          onOpenChange={setGenerateTempPasswordModalOpen}
          onGenerate={writes.regenerateTempPassword}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <ResendWelcomeEmailDialog
          open={resendWelcomeEmailDialogOpen}
          user={selectedUser}
          onOpenChange={setResendWelcomeEmailDialogOpen}
          onResendWelcomeEmail={writes.resendWelcomeEmail}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <DeleteUserDialog
          open={deleteDialogOpen}
          user={selectedUser}
          onOpenChange={setDeleteDialogOpen}
          onDeleteUser={writes.deleteUser}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <PermanentDeleteUserDialog
          open={permanentDeleteDialogOpen}
          user={selectedUser}
          onOpenChange={setPermanentDeleteDialogOpen}
          onPermanentDeleteUser={writes.permanentDeleteUser}
        />
      </ErrorBoundary>
    </>
  );
}
